import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { StatsAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { ArrowLeft, QrCode, Trophy, Crown, User, Shield, Heart, Building2 } from "lucide-react";

const KIND_META = {
  player: { label: "Joueur", icon: User, color: "#0066FF", path: (s) => `/player/${s}` },
  team: { label: "Équipe", icon: Shield, color: "#C0C0C0", path: (s) => `/team/${s}` },
  tournament: { label: "Tournoi", icon: Trophy, color: "#D4AF37", path: (s) => `/tournament/${s}` },
  championship: { label: "Championnat", icon: Crown, color: "#D4AF37", path: (s) => `/championship/${s}` },
  association: { label: "Association", icon: Heart, color: "#E25555", path: (s) => `/association/${s}` },
  company: { label: "Entreprise", icon: Building2, color: "#4FB286", path: (s) => `/entreprise/${s}` },
};

const FILTERS = [
  { value: "all", label: "Tout" },
  { value: "player", label: "Joueurs" },
  { value: "team", label: "Équipes" },
  { value: "tournament", label: "Tournois" },
  { value: "championship", label: "Championnats" },
  { value: "association", label: "Associations" },
  { value: "company", label: "Entreprises" },
];

export default function AdminScanStats() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    StatsAPI.views()
      .then(setRows)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = filter === "all" ? rows : rows.filter((r) => r.kind === filter);
  const totals = visible.reduce(
    (a, r) => ({ total: a.total + r.total, last7: a.last7 + r.last7, last30: a.last30 + r.last30 }),
    { total: 0, last7: 0, last30: 0 },
  );
  const presentKinds = new Set(rows.map((r) => r.kind));

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-4xl mx-auto w-full px-4 md:px-8 py-10 md:py-16 pb-mobile-safe">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body mb-6"
          data-testid="scans-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <QrCode className="w-8 h-8 text-[#D4AF37]" />
          <h1 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tighter leading-none">
            Scans <span className="text-gradient-gold">& vues</span>
          </h1>
        </div>
        <p className="text-zinc-400 font-body mb-8">
          Chaque ouverture d'une page publique (scan QR, NFC ou lien) est comptée — une fois par visite.
        </p>

        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: "Total", value: totals.total },
            { label: "7 derniers jours", value: totals.last7 },
            { label: "30 derniers jours", value: totals.last30 },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-[#111216] border border-white/10 rounded-xl p-4 md:p-5"
              data-testid={`scans-total-${s.label}`}
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-body mb-1">
                {s.label}
              </div>
              <div className="font-display text-3xl md:text-4xl font-black text-[#D4AF37] tabular-nums">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.filter((f) => f.value === "all" || presentKinds.has(f.value)).map((f) => (
            <button
              key={f.value}
              type="button"
              data-testid={`scans-filter-${f.value}`}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold border transition-colors ${
                filter === f.value
                  ? "border-[#D4AF37] text-black"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
              style={
                filter === f.value
                  ? { background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)" }
                  : {}
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-zinc-500 font-body">Chargement...</div>
        ) : visible.length === 0 ? (
          <div
            className="bg-[#111216] border border-dashed border-white/10 rounded-xl p-10 text-center text-zinc-500 font-body"
            data-testid="scans-empty"
          >
            Aucun scan enregistré pour le moment. Les compteurs démarrent dès qu'une page publique est ouverte.
          </div>
        ) : (
          <div
            className="bg-[#111216] border border-white/10 rounded-xl overflow-hidden"
            data-testid="scans-table"
          >
            <div className="grid grid-cols-[1fr,52px,52px,60px] sm:grid-cols-[1fr,80px,80px,90px] gap-2 px-3 sm:px-5 py-3 border-b border-white/10 text-[9px] sm:text-[11px] font-display font-bold uppercase tracking-[0.18em] text-zinc-500">
              <div>Page</div>
              <div className="text-right">7 j</div>
              <div className="text-right">30 j</div>
              <div className="text-right text-[#D4AF37]">Total</div>
            </div>
            {visible.map((r, i) => {
              const meta = KIND_META[r.kind] || KIND_META.player;
              const Icon = meta.icon;
              const inner = (
                <>
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div
                      className="w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 border"
                      style={{ backgroundColor: `${meta.color}18`, borderColor: `${meta.color}44` }}
                    >
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: meta.color }} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display uppercase tracking-tight font-semibold text-xs sm:text-base leading-tight truncate">
                        {r.name}
                      </div>
                      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] font-body" style={{ color: meta.color }}>
                        {meta.label}
                      </div>
                    </div>
                  </div>
                  <div className="text-right font-display tabular-nums text-zinc-400 self-center">{r.last7}</div>
                  <div className="text-right font-display tabular-nums text-zinc-400 self-center">{r.last30}</div>
                  <div className="text-right font-display font-black tabular-nums text-base sm:text-lg text-[#D4AF37] self-center">
                    {r.total}
                  </div>
                </>
              );
              const cls =
                "grid grid-cols-[1fr,52px,52px,60px] sm:grid-cols-[1fr,80px,80px,90px] gap-2 px-3 sm:px-5 py-3 sm:py-4 border-b border-white/5 last:border-0 transition-colors";
              return r.exists ? (
                <Link
                  key={`${r.kind}-${r.slug}`}
                  to={meta.path(r.slug)}
                  className={`${cls} hover:bg-white/[0.06]`}
                  data-testid={`scans-row-${i}`}
                >
                  {inner}
                </Link>
              ) : (
                <div key={`${r.kind}-${r.slug}`} className={cls} data-testid={`scans-row-${i}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
